import React from 'react';
import { TimelineArticle } from '../types/timeline';
import { SupportedLanguage } from '../types/language';
import { useMultilingual } from '../hooks/useMultilingual';
import { format } from 'date-fns';
import { 
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  FireIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { Modal } from './common/Modal';
import { useTranslation } from 'react-i18next';

interface ArticleDetailModalProps {
  article: TimelineArticle;
  language?: SupportedLanguage;
  onClose: () => void;
}

export const ArticleDetailModal: React.FC<ArticleDetailModalProps> = ({
  article,
  language = 'en',
  onClose,
}) => {
  const { getMultilingualContent, getMultilingualArray } = useMultilingual(language);
  const { t } = useTranslation();

  const getRiskLabel = (score: number) => {
    if (score >= 8) return t('public.riskLevels.critical').toUpperCase();
    if (score >= 6) return t('public.riskLevels.high').toUpperCase();
    if (score >= 4) return t('public.riskLevels.medium').toUpperCase();
    return t('public.riskLevels.low').toUpperCase();
  };

  const categoryColors: Record<string, string> = {
    MILITARY: 'bg-red-100 text-red-800',
    CYBER: 'bg-purple-100 text-purple-800',
    ECONOMIC: 'bg-blue-100 text-blue-800',
    DIPLOMATIC: 'bg-green-100 text-green-800',
    INFORMATION_WARFARE: 'bg-yellow-100 text-yellow-800',
  };

  return (
    <Modal isOpen={true} onClose={onClose} panelClassName="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
          <div className="flex justify-between items-start">
            <div className="flex-1 pr-4">
              <h2 className="text-2xl font-bold mb-2 leading-tight">{article.title}</h2>
              <div className="flex items-center gap-4 text-blue-100">
                <span>{article.source}</span>
                <span>•</span>
                <span>{format(new Date(article.publishedAt), 'PPp')}</span>
                {article.isHighImpact && (
                  <>
                    <span>•</span>
                    <span className="flex items-center gap-1 text-orange-200">
                      <FireIcon className="w-4 h-4" />
                      {t('public.articleDetail.highImpact')}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className={`px-4 py-2 rounded-lg border-2 border-white/20 bg-white/10`}>
                <div className="text-3xl font-bold text-white">{article.overallRiskScore.toFixed(1)}</div>
                <div className="text-xs font-medium text-blue-100">{getRiskLabel(article.overallRiskScore)}</div>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors p-1"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6 space-y-6">
          {/* Categories */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('public.articleDetail.threatCategories')}</h3>
            <div className="flex flex-wrap gap-2">
              {article.categories.map((category) => (
                <span
                  key={category}
                  className={`inline-block px-3 py-1 text-sm font-medium rounded-lg ${
                    categoryColors[category] || 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {category.replace(/_/g, ' ')}
                </span>
              ))}
              {article.impactPercentage && article.impactPercentage > 20 && (
                <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-lg bg-purple-100 text-purple-800">
                  <SparklesIcon className="w-4 h-4" />
                  {t('public.articleDetail.percentOfDayRisk', { percent: article.impactPercentage.toFixed(0) })}
                </span>
              )}
            </div>
          </div>

          {/* Key Points */}
          {getMultilingualArray(article.keyPoints).length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('public.articleDetail.keyPoints')}</h3>
              <ul className="space-y-2">
                {getMultilingualArray(article.keyPoints).map((point, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-500 mr-3 mt-1">•</span>
                    <span className="text-gray-700">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Summary */}
          {getMultilingualContent(article.summary) && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('public.articleDetail.analysisSummary')}</h3>
              <p className="text-gray-700 leading-relaxed">{getMultilingualContent(article.summary)}</p>
            </div>
          )}

          {/* Risk Analysis Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('public.articleDetail.riskAssessment')}</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-700">{t('public.articleDetail.overallRiskScore')}</span>
                <span className={`font-bold text-2xl ${
                  article.overallRiskScore >= 7 ? 'text-red-600' : 
                  article.overallRiskScore >= 5 ? 'text-orange-600' : 
                  article.overallRiskScore >= 3 ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  {article.overallRiskScore.toFixed(1)}/10
                </span>
              </div>
              <div className="text-sm text-gray-600">
                {t('public.articleDetail.classifiedAs', { level: getRiskLabel(article.overallRiskScore).toLowerCase() })}
              </div>
            </div>
          </div>

          {/* Decay Information for Lingering Threats */}
          {article.daysAgo && article.daysAgo > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-orange-800 mb-2 flex items-center">
                <FireIcon className="w-5 h-5 mr-2" />
                {t('public.articleDetail.ongoingImpact')}
              </h3>
              <div className="text-sm text-orange-700 space-y-1">
                <p><strong>{t('public.articleDetail.originallyReported')}</strong> {article.daysAgo} {article.daysAgo === 1 ? t('public.articleDetail.day') : t('public.articleDetail.days')} {t('public.articleDetail.ago')}</p>
                <p><strong>{t('public.articleDetail.originalRiskScore')}</strong> {article.overallRiskScore.toFixed(1)}</p>
                {article.decayedScore && (
                  <p><strong>{t('public.articleDetail.currentImpact')}</strong> {article.decayedScore.toFixed(1)} ({t('public.articleDetail.percentRemaining', { defaultValue: '{{percent}}% {{remaining}}', percent: ((article.decayFactor || 0) * 100).toFixed(0), remaining: t('public.articleDetail.remaining') })})</p>
                )}
                <p className="text-xs mt-2">{t('public.articleDetail.highRiskNote')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 p-6 flex justify-between items-center">
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            {t('public.articleDetail.readFullArticle')}
            <ArrowTopRightOnSquareIcon className="w-5 h-5" />
          </a>
          <button
            onClick={onClose}
            className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            {t('public.articleDetail.close')}
          </button>
        </div>
    </Modal>
  );
};
